import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CardDbHashType } from './cardDbSlice';
import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type ImageDb = {
  authorId: string;
  dataUrl: string;
  goodsId?: string;
  version?: number;
}
const initial: ImageDb = {
  authorId: '',
  dataUrl: '',
  goodsId: undefined,
  version: undefined
};

export type ImageDbHashType = {
  [key in string]: ImageDb;
}
type ImageMakerHashType = {
  [key in string]: boolean;
}
export type ImageAddPayload = {
  id?: string;
} & ImageDb;
export type ImageUpdatePayload = {
  id: string;
} & { [P in keyof ImageDb]?: ImageDb[P] };
type ImageLoadPayload = {
  id: string;
} & ImageDb;

export type ImageDeleteUnusedPayload = CardDbHashType;

export type ImageDbState = {
  images: ImageDbHashType;
}

export const generateImageId = () : string => generateId('i');
let initialState: ImageDbState = { images: {} };
const key = 'images';

const ImageDbSlice = createSlice({
  name: 'ImageDb',
  initialState,
  reducers: {
    initializeImages(state: ImageDbState) {
      state.images = {};
    },
    addImage(state: ImageDbState, action: PayloadAction<ImageAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateImageId();
      state.images[id] = obj;
      saveData(key, id, obj);
    },
    updateImage(state: ImageDbState, action: PayloadAction<ImageUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.images[id] === undefined) {
        console.log(`*** Image not found: ${id}`);
      } else {
        const newData = { ...state.images[id], ...obj };
        state.images[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteImage(state: ImageDbState, action: PayloadAction<ImageUpdatePayload>) {
      const { id, authorId } = action.payload;
      const target = state.images[id];
      delete state.images[id];
      if (!authorId || authorId !== target.authorId) return;
      deleteData(key, id);
    },
    // deleteUnusedImages(state: ImageDbState, action: PayloadAction<ImageDeleteUnusedPayload>) {
    //   let usedMarker: ImageMakerHashType = {};
    //   // Object.keys(state.images).forEach(imageId => {
    //   //   if (state.images[imageId].name) usedMarker[imageId] = true;
    //   // })
    //   // mark used images
    //   Object.values(action.payload).forEach(card => {
    //     const imageId = getCardImage(card);
    //     if (imageId) usedMarker[imageId] = true;
    //   })
    //   // delete unused images
    //   Object.keys(state.images).forEach(imageId => {
    //     if (!usedMarker[imageId]) {
    //       console.log(`--- delete Unused image: ${imageId}`);
    //       delete state.images[imageId];
    //     }
    //   })
    // },
    saveImages(state: ImageDbState, action: PayloadAction<void>) {
      saveAllData(key, state.images);
    },
    signUpImages(state: ImageDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.images, action.payload);
      const targetIds = Object.keys(values).filter(id => !values[id].goodsId);
      const ownImages: ImageDbHashType = {};
      targetIds.forEach(id => ownImages[id] = values[id])
      addDatasToSave(key, ownImages);
    },
    loadImage(state: ImageDbState, action: PayloadAction<ImageLoadPayload>) {
      const { id, ...value } = action.payload;
      if (state.images[id]) {
        console.log(`image ${id} exists`);
        return;
      }
      state.images[id] = value;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: ImageDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.images = updatedValues;
      if (changed) saveAllData(key, state.images);
    })
  }
})

export const { initializeImages, addImage, updateImage, deleteImage, saveImages, signUpImages, loadImage } = ImageDbSlice.actions;
export default ImageDbSlice.reducer;
